import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { StorageKey } from '../models/common';

/** Services */
import { UserService } from '../services/user.service';
import { UserContextRefreshService } from '../services/user-context-refresh.service';
import { ContextService } from '../services/context.service';
import { StorageService } from '../services/storage.service';
import { SessionStorageService } from '../services/sessions-storage.service';
import {
  isExternalPaymentDetailsInvite,
  isVendorCreateRoute,
  resolveInviteId,
} from '../utils/invite-id.util';

@Injectable()
export class ContextGuard implements CanActivate {
  constructor(
    private userSvc: UserService,
    private contextSvc: ContextService,
    private storageSvc: StorageService,
    private router: Router,
    private sessionStorageService: SessionStorageService,
    private userContextRefreshSvc: UserContextRefreshService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const isVendorPaymentDetailLink = isExternalPaymentDetailsInvite(
      state.url,
      route.queryParams,
      route.queryParamMap
    );
    const inviteId = resolveInviteId(route, state.url);
    const isVendorInviteLink =
      !!inviteId && isVendorCreateRoute(state.url) && !isVendorPaymentDetailLink;

    const isExternalAssetForm = state.url.includes('/asset-management/new-asset');

    if (isVendorInviteLink || isVendorPaymentDetailLink || isExternalAssetForm) {
      return new Observable<boolean>((observer) => {
        observer.next(true);
        observer.complete();
      });
    }

    return this.userContextRefreshSvc.refresh().pipe(
      map(() => true),
      catchError(() => {
        this.storageSvc.remove(StorageKey.jwt);
        this.storageSvc.remove(StorageKey.refreshToken);
        // Return a UrlTree instead of calling router.navigateByUrl() + of(false).
        // Navigating imperatively while this guard is still resolving the current
        // navigation races the Router's internal transition and can leave it stuck.
        return of(this.router.createUrlTree(['/auth/error']));
      })
    );
  }
}
